import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useContext, useState } from 'react';
import AuthContext from '../../contexes/AuthContext';
import { useSnackBar } from '../../hooks/useSnackBar';
import { useTranslation } from '../../hooks/useTranslation';
import User from '../../interfaces/User';
import adminService from '../../services/adminService';
import { getCompany } from '../../services/companyService';
import { getUserByEmail } from '../../services/userService';
import Button from '../shared/Button/Button';

interface TransferAgentDialogProps {
  agentId: string;
  agentName: string;
  agentType: 'call' | 'chat';
  onSuccess: () => void;
  onClose: () => void;
}

interface RecipientDetails {
  user: User;
  companyName: string;
}

const TransferAgentDialog: React.FC<TransferAgentDialogProps> = ({
  agentId,
  agentName,
  agentType,
  onSuccess,
  onClose,
}) => {
  const { currentUser } = useContext(AuthContext);
  const { t } = useTranslation();
  const showSnackbar = useSnackBar();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [recipient, setRecipient] = useState<RecipientDetails | null>(null);

  const handleClose = () => {
    setEmail('');
    setEmailError('');
    setRecipient(null);
    onClose();
  };

  const handleTransfer = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setEmailError('');
    try {
      const userResponse = await getUserByEmail(email.trim());
      const user: User = userResponse.message;
      let companyName = '';
      if (user.company_id) {
        const companyResponse = await getCompany(user.company_id);
        companyName = companyResponse?.message?.name ?? '';
      }
      setRecipient({ user, companyName });
    } catch {
      setEmailError(
        t('No account found with that email address. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setRecipient(null);
    setEmailError('');
  };

  const handleConfirm = async () => {
    if (!currentUser || !recipient) return;
    setLoading(true);
    try {
      await adminService.transferAgent(
        agentId,
        agentType,
        recipient.user.id,
        currentUser.id
      );
      showSnackbar(
        t('Agent "{{agentName}}" transferred successfully.', { agentName }),
        'success'
      );
      handleClose();
      onSuccess();
    } catch {
      showSnackbar(t('Agent transfer failed. Please try again.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const agentTypeLabel = t(agentType === 'call' ? 'call agent' : 'chat agent');

  return (
    <Dialog open onClose={handleClose} fullWidth maxWidth="sm">
      {recipient ? (
        <>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {t(
              'Transfer the {{agentTypeLabel}} "{{agentName}}" to a client\'s account',
              { agentTypeLabel, agentName }
            )}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1} mt={1}>
              <Typography variant="body2" color="error" fontWeight={500}>
                {t('Verify recipient details. This process is irreversible.')}
              </Typography>
              <Stack spacing={0.5} mt={1}>
                <Typography variant="body2">
                  {t('Name')}:{' '}
                  <Typography component="span" variant="body2" fontWeight={700}>
                    {recipient.user.name || '—'}
                  </Typography>
                </Typography>
                <Typography variant="body2">
                  {t('Email Address')}:{' '}
                  <Typography component="span" variant="body2" fontWeight={700}>
                    {recipient.user.email}
                  </Typography>
                </Typography>
                <Typography variant="body2">
                  {t('Company')}:{' '}
                  <Typography component="span" variant="body2" fontWeight={700}>
                    {recipient.companyName || '—'}
                  </Typography>
                </Typography>
                <Typography variant="body2">
                  {t('User ID')}:{' '}
                  <Typography component="span" variant="body2" fontWeight={700}>
                    {recipient.user.id}
                  </Typography>
                </Typography>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={handleBack}
              disabled={loading}
            >
              {t('Back')}
            </Button>
            <Button
              size="small"
              color="error"
              disabled={loading}
              onClick={handleConfirm}
            >
              {t('Confirm')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {t(
              'Transfer the {{agentTypeLabel}} "{{agentName}}" to a client\'s account',
              { agentTypeLabel, agentName }
            )}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1} mt={1}>
              <Typography variant="body2" color="text.secondary">
                {t("Search user's account via email address")}
              </Typography>
              <TextField
                placeholder={t('email@address.com')}
                value={email}
                onChange={event => {
                  setEmail(event.target.value);
                  setEmailError('');
                }}
                fullWidth
                autoFocus
                type="email"
                error={!!emailError}
                helperText={emailError}
                onKeyDown={event => event.key === 'Enter' && handleTransfer()}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={handleClose}
              disabled={loading}
            >
              {t('Cancel')}
            </Button>
            <Button
              size="small"
              disabled={!email.trim() || loading}
              onClick={handleTransfer}
            >
              {t('Transfer')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default TransferAgentDialog;
